<template>
  <div class="w-full flex flex-col p-6">
    <div class="flex flex-col mx-auto w-full" style="max-width: 900px;">
      <!-- Header/Title -->
      <div class="flex flex-col gap-1 mt-6">
        <BaseText type="label" size="md" class="text-text-muted">
          Integrations
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          Connect your Foreplay account to the tools you love.
        </BaseText>
      </div>
      <!-- divider -->
      <div class="w-full h-0 my-5 border-b border-border-normal" />
      <!-- SWIPE FILE INTEGRATIONS -->
      <div class="w-full flex flex-col gap-1">
        <BaseText type="label" size="sm" class="text-text-muted">
          Swipe File Integrations
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          These connections will be used to save ads from anywhere to Swipe File.
        </BaseText>
      </div>
      <!-- INSTAGRAM -->
      <div class="w-full flex flex-col p-1 border border-border-normal rounded-2xl mt-4">
        <!-- Header -->
        <div class="w-full flex items-center justify-between p-2">
          <div class="flex items-center gap-1.5">
            <div class="w-8 h-8 flex items-center justify-center">
              <InstagramPlatformIcon :width="16" :height="16" />
            </div>
            <BaseText type="label" size="sm" class="text-text-muted pr-2">
              Instagram
            </BaseText>
            <!-- Number of connections -->
            <div v-if="!igIntegrationUsers?.length" class="skeleton rounded-md" style="width: 96px; height: 20px;" />
            <BaseText v-else type="label" size="xs" class="text-text-muted px-1.5 py-0.5 rounded-md bg-neutral-25 fade-in">
              {{ igIntegrationUsers?.length }} {{ igIntegrationUsers?.length === 1 ? 'Connection' : 'Connections' }}
            </BaseText>
          </div>
          <!-- Add Connection Button -->
          <BaseTooltipV2 :tooltip-enabled="isGuestOnly"
          text="Instagram integrations require a subscription">
            <button class="secondary-btn flex items-center gap-1.5 px-2 py-1.5 rounded-md" :class="{'disabled': isGuestOnly}"
            @click="showIgModal = true" :disabled="isGuestOnly">
              <MonoIGStrokeIcon :class="isGuestOnly ? 'text-icon-disabled' : 'text-icon-normal'" />
              <BaseText type="label" size="sm" :class="isGuestOnly ? 'text-text-disabled' : 'text-text-muted'">
                {{ igConnectionButtonText }}
              </BaseText>
            </button>
          </BaseTooltipV2>
        </div>
        <!-- Table Header -->
        <div class="connections-grid w-full px-3 py-2 rounded-md bg-neutral-25 table-header-text mt-1">
          <div>USER</div>
          <div>STATUS</div>
          <div>IG HANDLE</div>
          <div>LINKED BOARDS</div>
        </div>
        <!-- Table Body -->
        <div class="w-full flex flex-col gap-1 mt-1">
          <!-- Load Skeletons -->
          <template v-if="!igIntegrationUsers.length" v-for="n in getTeam?.members?.length || 1">
            <div class="relative left-2 skeleton rounded-md h-9 opacity-70 my-2" style="width: calc(100% - 16px)" :key="`ig-skeleton-${n}`" />
            <div v-if="n !== getTeam?.members?.length" :key="`ig-divider-${n}`"
            class="relative left-2 h-0 border-b border-border-normal" style="width: calc(100% - 16px)" />
          </template>
          <template v-for="(user, index) in igIntegrationUsers">
            <IGAccountRow :user="user" :key="`ig-account-row-${user.id}`" />
            <div v-if="index !== igIntegrationUsers.length - 1" :key="`ig-divider-${index}`"
            class="relative left-2 h-0 border-b border-border-normal" style="width: calc(100% - 16px)" />
          </template>
        </div>
      </div>
      <!-- divider -->
      <div class="w-full h-0 my-6 border-b border-border-normal" />
      <!-- LENS INTEGRATIONS / DATA SOURCES -->
      <div class="w-full flex flex-col gap-1">
        <BaseText type="label" size="sm" class="text-text-muted">
          Lens Integrations / Data Sources
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-normal">
          These connections will be used to load in ad account data. You can authenticate one or multiple platform connections.
        </BaseText>
      </div>
      <!-- Platform Cards -->
      <div class="w-full flex flex-col gap-4 mt-4">
        <div v-for="platform in lensPlatforms" :key="`lens-platform-${platform.key}`"
        class="w-full flex flex-col p-1 rounded-2xl border border-border-normal">
          <!-- Header -->
          <div class="w-full flex items-center justify-between p-2">
            <div class="flex items-center gap-1.5">
              <!-- Platform Icon -->
              <div class="w-8 h-8 flex items-center justify-center">
                <component :is="platform.icon" :width="16" :height="16" />
              </div>
              <!-- Name & Number of Connections -->
              <BaseText type="label" size="sm" class="text-text-muted pr-2">
                {{ platform.label }}
              </BaseText>
              <!-- Number of Connections -->
              <div v-if="loadingSources" class="skeleton rounded-md" style="width: 96px; height: 20px;" />
              <BaseText v-else type="label" size="xs" class="text-text-muted px-1.5 py-0.5 rounded-md bg-neutral-25 fade-in">
                {{ platform?.numConnections ?? 0 }} {{ platform?.numConnections === 1 ? 'Connection' : 'Connections' }}
              </BaseText>
            </div>
            <!-- Add Connection Button -->
            <AddLensPlatformAccountButton
              v-if="!loadingSources && platform.accounts.length"
              class="fade-in"
              :icon="platform.monoIcon"
              :platform-name="platform.name"
              :loading="loadingLensAuth[platform.key]"
              @authenticate="handlePlatformAuthenticate(platform.key)"
            />
          </div>
          <div class="divider" />
          <!-- Connected Platform Accounts -->
          <!-- Loading State -->
          <div v-if="loadingSources" class="skeleton w-full rounded-md fade-in" style="height: 52px" />
          <!-- No Accounts State -->
          <div v-else-if="!platform.accounts.length" class="w-full flex flex-col items-center p-7">
            <component :is="platform.monoIcon" class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted mt-3">
              No {{ platform.name }} Accounts
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal mt-1">
              You haven't connected any {{ platform.name }} accounts to Foreplay.
            </BaseText>
            <AddLensPlatformAccountButton
              class="mt-5"
              :icon="platform.monoIcon"
              :platform-name="platform.name"
              :loading="loadingLensAuth[platform.key]"
              @authenticate="handlePlatformAuthenticate(platform.key)"
            />
          </div>
          <!-- Connected Accounts List -->
          <template v-else v-for="(account, index) in platform.accounts">
            <DataSourceCard 
              :key="`${platform.key}-${account.id}`"
              class="fade-in"
              :platform="platform.key"
              :account="account"
              @refresh-ad-accounts="getTeamPlatformAccounts"
            />
            <div v-if="index !== platform.accounts.length - 1" class="divider" :key="`${platform.key}-${account.id}-divider`" />
          </template>
        </div>
      </div>
    </div>
    <!-- Connect Instagram Modal -->
    <ConnectInstagramModal 
      v-if="showIgModal" 
      @close="showIgModal = false"
      @refresh="initializeIgUsers"
    />
    <!-- Lens Platform Authentication Controller Interface -->
    <LensPlatformAuthenticator 
      ref="lensPlatformAuthenticator"
      :loading="lensAuthLoading"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { formatProvidersByPlatform } from '@/utils/lens/dataSourceUtils'
import firebase from '../../../api/config/FirebaseConfig'
import FirebaseAPI from '@/api/firebase'
import LensAPI from '@/api/lens'

// Controllers
import LensPlatformAuthenticator from '../../lens/LensPlatformAuthenticator.vue'

// Components
import IGAccountRow from './IGAccountRow.vue'
import ConnectInstagramModal from './ConnectInstagramModal.vue'
import DataSourceCard from '../lens/DataSourceCard.vue'
import AddLensPlatformAccountButton from './AddLensPlatformAccountButton.vue'

// Icons
import InstagramPlatformIcon from '../../globals/Icons/FilterIcons/Platforms/InstagramPlatformIcon.vue'
import MonoIGStrokeIcon from '../../globals/Icons/socials/mono/MonoIGStrokeIcon.vue'
import MetaPlatformIcon from '../../globals/Icons/FilterIcons/Platforms/MetaPlatformIcon.vue'
import TikTokPlatformIcon from '../../globals/Icons/FilterIcons/Platforms/TikTokPlatformIcon.vue'
import MonoMetaIcon from '../../globals/Icons/socials/mono/MonoMetaIcon.vue'
import MonoTikTokIcon from '../../globals/Icons/socials/mono/MonoTiktokIcon.vue'

export default {
  name: 'IntegrationSettings',
  components: {
    LensPlatformAuthenticator,
    IGAccountRow,
    ConnectInstagramModal,
    DataSourceCard,
    AddLensPlatformAccountButton,
    InstagramPlatformIcon,
    MonoIGStrokeIcon
  },
  data () {
    return {
      // Team
      teamMembers: [],
      // Instagram
      igIntegrationUsers: [],
      showIgModal: false,
      // Lens
      teamMembers: [],
      lensPlatformAccounts: {
        meta: [],
        tiktok: []
      },
      loadingSources: true,
      loadingLensAuth: {
        meta: false,
        tiktok: false
      }
    }
  },
  async mounted () {
    await this.getTeamMembers()
    this.initializeIgUsers()
    this.getTeamPlatformAccounts()
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser', 'getTeam', 'getGuestStatus']),
    lensPlatforms () {
      return [
        {
          key: 'meta',
          name: 'Meta',
          label: 'Meta Ads',
          icon: MetaPlatformIcon,
          monoIcon: MonoMetaIcon,
          accounts: this.enrichedLensPlatformAccounts.meta,
          numConnections: this.enrichedLensPlatformAccounts.meta.length
        },
        {
          key: 'tiktok',
          name: 'TikTok',
          label: 'TikTok Ads',
          icon: TikTokPlatformIcon,
          monoIcon: MonoTikTokIcon,
          accounts: this.enrichedLensPlatformAccounts.tiktok,
          numConnections: this.enrichedLensPlatformAccounts.tiktok.length
        }
      ]
    },
    enrichedLensPlatformAccounts () {
      const enrichAccounts = (accounts) => {
        return accounts.map(account => ({
          ...account,
          ownerUser: this.teamMembers.find(member => member.id === account.createdBy) ?? null
        }))
      }
      return {
        meta: enrichAccounts(this.lensPlatformAccounts.meta),
        tiktok: enrichAccounts(this.lensPlatformAccounts.tiktok)
      }
    },
    igConnectionButtonText () {
      if (this.getUser.igUsername && this.getUser.igWaitingVerification) {
        return 'Verify Connection'
      }
      if (this.getUser.igUsername) {
        return 'Edit Connection'
      }
      return 'Connect New IG'
    },
    lensAuthLoading () {
      return Object.values(this.loadingLensAuth).some(loading => loading)
    },
    isGuestOnly () {
      return this.getGuestStatus?.isGuestOnly ?? false
    }
  },
  methods: {
    ...mapMutations('AuthModule', ['SET_USER']),
    async getTeamMembers () {
      if (this.getTeam) {
        this.teamMembers = await FirebaseAPI.Teams.getTeamMembers()
      }
    },
    async initializeIgUsers () {
      this.igIntegrationUsers = []
      // We need to refetch the user object in case the verification status has changed
      const user = firebase.auth().currentUser
      const currentUserData = await FirebaseAPI.Users.getById(user.uid)
      this.SET_USER({ ...this.getUser, ...currentUserData })
      if (this.getTeam) {
        // Put the current user first in the list
        this.igIntegrationUsers = [
          this.getUser,
          ...[...this.teamMembers].filter(member => member.user_id !== user.uid)
        ]
      } else {
        this.igIntegrationUsers = [this.getUser]
      }
    },
    async getTeamPlatformAccounts () {
      this.loadingSources = true
      try {
        const { data: allProviders } = await LensAPI.Lens.getPlatformAccounts()
        this.lensPlatformAccounts = formatProvidersByPlatform(allProviders)
      } catch (error) {
        console.error('Failed to get team platform accounts:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to fetch platform accounts. See console for details.'
        })
      } finally {
        this.loadingSources = false
      }
    },
    async handlePlatformAuthenticate (platform) {
      this.loadingLensAuth[platform] = true
      try {
        await this.$refs.lensPlatformAuthenticator.handlePlatformAuthenticate(platform)
        this.getTeamPlatformAccounts()
      } catch (error) {
        console.error('Failed to authenticate platform:', error)
      } finally {
        this.loadingLensAuth[platform] = false
      }
    },
  }
}
</script>

<style scoped>
.connections-grid {
  display: grid;
  grid-template-columns: 216px 136px 160px 1fr;
  column-gap: 8px;
  align-items: center;
}
.table-header-text {
  color: #5C667A;

  /* Heading/Overline */
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px; /* 133.333% */
  letter-spacing: 0.72px;
  text-transform: uppercase;
}
.divider {
  position: relative;
  left: 8px;
  width: calc(100% - 16px);
  height: 0;
  border-bottom: 1px solid #ECEFF3;
  margin-top: 6px;
  margin-bottom: 6px;
}

.secondary-btn {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08);
  background-color: white;
  color: #303546;
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out, color 100ms ease-in-out;
}
.secondary-btn:hover, .secondary-btn.disabled {
  box-shadow: none;
  background-color: #F6F8FA;
}
.secondary-btn.disabled {
  color: #A4ABB8;
  cursor: default;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>